/**
 * Admin — the card-2260 stored-economics reconciliation block.
 *
 * FIVE surfaces publish the versioned `order_economics` money lines. This renders them over ONE
 * canonical population and ONE window, each with its own population count beside its figures.
 *
 * 🪤 THE HEADLINE IS `discrepancyCount`, NOT the surface pass count. Three of the five DERIVE net
 *   and profit (`gross − reserve − expense`) while two render the STORED columns verbatim, so ONE
 *   bad row turns TWO surfaces red. Counting red surfaces would report that single defect as two.
 *
 * ⛔ Deltas are exact cents from the API and are never rounded toward agreement in the view.
 */

import { formatPrice } from '../../utils/formatters';
import styles from '../../pages/admin/RevenueCalculationPage.module.css';

/** Pure. A signed dollar delta. Zero renders as an explicit $0.00, never as a blank. */
export const DeltaCell = ({ cents }) => {
  const value = Number(cents ?? 0);
  const className = value === 0 ? styles.deltaZero : styles.deltaNonZero;
  return (
    <span className={className}>
      {value > 0 ? '+' : ''}{formatPrice(value)}
    </span>
  );
};

/** The five money lines, in the order every surface presents them. */
const MONEY_ROWS = [
  { key: 'grossRevenueCents', label: 'Gross revenue' },
  { key: 'reserveWithheldCents', label: 'Reserve withheld (10%)' },
  { key: 'netRevenueCents', label: 'Net revenue' },
  { key: 'totalExpenseCents', label: 'Total expense' },
  { key: 'profitCents', label: 'Profit' }
];

/** Pure. A money line and its exact-cent delta against the canonical reading. */
const FigureLine = ({ label, cents, delta }) => (
  <>
    <dt className={styles.reconTerm}>{label}</dt>
    <dd className={styles.reconDef}>
      {formatPrice(cents ?? 0)} · delta <DeltaCell cents={delta} />
    </dd>
  </>
);

/**
 * Pure. One of the five stored-economics surfaces.
 *
 * The population count is rendered NEXT TO the figures on purpose (criterion 5): without it a
 * reader cannot tell a mismatch caused by a different population from an arithmetic one.
 */
export const EconomicsSurfaceRow = ({ row }) => (
  <li
    className={row.status === 'PASS' ? styles.reconRowPass : styles.reconRowFail}
    data-testid={`recon-economics-row-${row.key}`}
    data-status={row.status}
    data-reads={row.reads}
  >
    <div className={styles.reconHead}>
      <span
        className={row.status === 'PASS' ? styles.reconBadgePass : styles.reconBadgeFail}
        data-testid={`recon-economics-status-${row.key}`}
      >
        {row.status}
      </span>
      <span className={styles.reconLabel}>{row.label}</span>
      <span className={styles.reconPopulation} data-testid={`recon-economics-population-${row.key}`}>
        {row.orderCount} of {row.populationOrderCount} canonical order
        {row.populationOrderCount === 1 ? '' : 's'}
        {row.ordersWithoutEconomics > 0 ? ` (${row.ordersWithoutEconomics} not yet recalculated)` : ''}
      </span>
    </div>

    <dl className={styles.reconGrid}>
      <dt className={styles.reconTerm}>Reads</dt>
      <dd className={styles.reconDef} data-testid={`recon-economics-reads-${row.key}`}>
        {row.reads === 'stored'
          ? 'the STORED net_revenue / profit columns, verbatim'
          : 'net and profit DERIVED as gross − reserve − expense'}
      </dd>

      <dt className={styles.reconTerm}>Arithmetic</dt>
      <dd className={styles.reconDef}>{row.arithmetic}</dd>

      <dt className={styles.reconTerm}>Source</dt>
      <dd className={styles.reconDefMono}>{row.source}</dd>

      {MONEY_ROWS.map(({ key, label }) => (
        <FigureLine key={key} label={label} cents={row.figures?.[key]} delta={row.deltas?.[key]} />
      ))}
    </dl>
  </li>
);

/** Pure. One named per-order defect. */
export const DiscrepancyRow = ({ finding }) => (
  <li className={styles.reconRowFail} data-testid={`recon-discrepancy-${finding.orderId}-${finding.line}`}>
    <div className={styles.reconHead}>
      <span className={styles.reconBadgeFail}>{finding.line}</span>
      <span className={styles.reconLabel}>{finding.orderNumber ?? finding.orderId}</span>
      <span className={styles.reconPopulation}>
        stored {formatPrice(finding.storedCents)} · derived {formatPrice(finding.derivedCents)} · delta{' '}
        <DeltaCell cents={finding.deltaCents} />
      </span>
    </div>
    <dl className={styles.reconGrid}>
      <dt className={styles.reconTerm}>Column</dt>
      <dd className={styles.reconDefMono}>{finding.column} vs {finding.derivation}</dd>
      <dt className={styles.reconTerm}>Effect</dt>
      <dd className={styles.reconDef}>{finding.effect}</dd>
      <dt className={styles.reconTerm}>Published by</dt>
      <dd className={styles.reconDef}>{(finding.publishedBy ?? []).join('; ') || '—'}</dd>
      {(finding.invisibleTo ?? []).length > 0 && (
        <>
          <dt className={styles.reconTerm}>Invisible to</dt>
          <dd className={styles.reconDef}>{finding.invisibleTo.join('; ')}</dd>
        </>
      )}
    </dl>
  </li>
);

/** Pure. The card-2260 block. */
export const EconomicsReconciliation = ({ economics }) => {
  const {
    surfaces = [], discrepancies = [], discrepancyCount = 0, population = {},
    economicsVersion, storedSumsPresent = true, rowsWithUnresolvedChecks = 0
  } = economics ?? {};

  return (
    <div data-testid="recon-economics">
      <h3 className={styles.reconGroupTitle}>Stored economics — five surfaces</h3>

      <p
        className={discrepancyCount === 0 ? styles.reconVerdictPass : styles.reconVerdictFail}
        data-testid="recon-economics-verdict"
      >
        {discrepancyCount === 0
          ? `All ${surfaces.length} surfaces agree to the cent. 0 discrepancies.`
          : `${discrepancyCount} discrepanc${discrepancyCount === 1 ? 'y' : 'ies'} across ${surfaces.length} surfaces.`}
      </p>

      <p className={styles.reconLead}>
        <strong>{population.economicsRowCount ?? 0}</strong> of{' '}
        <strong>{population.orderCount ?? 0}</strong> canonical orders carry stored economics at v
        {economicsVersion ?? '?'}
        {population.ordersWithoutEconomics > 0
          ? ` — ${population.ordersWithoutEconomics} have never been recalculated and are reported as absent, never as $0.00.`
          : '.'}{' '}
        Population: {population.basis ?? 'unstated'}.
      </p>

      {/* Criterion 5 names a stricter population than the one the five surfaces publish. The
          difference is printed rather than resolved silently — it is an order-count difference
          and not a single cent, and that is a measurement, not an assumption. */}
      {population.alternateReading && (
        <p className={styles.reconGroupNote} data-testid="recon-economics-alternate-population">
          Alternate reading ({population.alternateReading}) would drop{' '}
          <strong>{population.cancelledOrRefundedCount ?? 0}</strong> order(s) from this window, of which{' '}
          <strong>{population.cancelledOrRefundedWithEconomics ?? 0}</strong> carry stored economics — so it
          changes the order count and not the money.
        </p>
      )}

      {!storedSumsPresent && (
        <p className={styles.reconVerdictFail} data-testid="recon-economics-shape-warning">
          The aggregate supplied to this panel carries no stored sums, so the two stored-reading surfaces
          cannot be graded. This is a wiring fault, not a data disagreement.
        </p>
      )}

      {rowsWithUnresolvedChecks > 0 && (
        <p className={styles.reconGroupNote} data-testid="recon-economics-unresolved">
          {rowsWithUnresolvedChecks} row(s) have at least one check whose inputs are absent. Those checks are
          counted as unresolved, never passed.
        </p>
      )}

      <ul className={styles.reconList} data-testid="recon-economics-surfaces">
        {surfaces.map((row) => <EconomicsSurfaceRow key={row.key} row={row} />)}
      </ul>

      {discrepancyCount > 0 && (
        <>
          <h3 className={styles.reconGroupTitle}>Named discrepancies</h3>
          <ul className={styles.reconList} data-testid="recon-economics-discrepancies">
            {discrepancies.map((finding) => (
              <DiscrepancyRow key={`${finding.orderId}-${finding.line}`} finding={finding} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default EconomicsReconciliation;
